import React, {useRef, useEffect, useState} from 'react';
import DOMPurify from 'dompurify';
import './CustomEditor.css';
import {enIE} from "date-fns/locale";

const exec = (command, value = null) => {
    document.execCommand(command, false, value);
};

const cleanWordHTML = function (html) {
    // Step 1: Basic cleanup using DOMPurify
    const cleanConfig = {
        ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li', 'table', 'tr', 'td', 'a', 'span', 'div'],
        ALLOWED_ATTR: ['style'],
        KEEP_CONTENT: true
    };

    // First pass with DOMPurify to remove malicious content
    html = DOMPurify.sanitize(html, cleanConfig);

    // Step 2: Remove MS Word-specific tags and comments
    html = html
        .replace(/<!--\[if.*?endif\]-->/gis, '')
        .replace(/<!--.*?-->/gis, '')
        .replace(/<o:p>|<\/o:p>/gi, '')
        .replace(/class=["']?Mso[a-zA-Z0-9]+["']?/gi, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\sclass=["'][^"']*["']/gi, '');

    // Step 3: Clean styles - keep only indentation
    html = html.replace(/\sstyle=["']([^"']*)["']/gi, function (match, styles) {
        var keepStyles = [];
        styles.split(';').forEach(function (s) {
            var trimmed = s.trim();
            if (
                (trimmed.startsWith('margin-left') || trimmed.startsWith('text-indent') ||
                    trimmed.startsWith('font-weight') || trimmed.startsWith('font-size')) &&
                !/-\d/.test(trimmed)
            ) {
                keepStyles.push(trimmed);
            }
        });
        return keepStyles.length ? ' style="' + keepStyles.join('; ') + '"' : '';
    });

    // Step 4: Remove unwanted tags but keep content
    html = html
        .replace(/<(span|font|div|section)[^>]*>/gi, '')
        .replace(/<\/(span|font|div|section)>/gi, '');

    // Step 5: Remove empty paragraphs
    html = html.replace(/<p>\s*<\/p>/gi, '');

    // Step 6: Process with DOM for list detection and header conversion
    var tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Convert lists first
    processListsInDOM(tempDiv);

    // Then convert headers and clean up
    var paragraphs = Array.from(tempDiv.querySelectorAll('p'));
    paragraphs.forEach(function (p) {
        var style = p.getAttribute('style') || '';
        var isBold = /font-weight\s*:\s*(bold|700)/i.test(style);
        var sizeMatch = style.match(/font-size\s*:\s*(\d+)pt/i);

        if (sizeMatch && isBold) {
            var size = parseInt(sizeMatch[1]);
            var headerTag = null;
            if (size >= 24) headerTag = 'H1';
            else if (size >= 20) headerTag = 'H2';
            else if (size >= 16) headerTag = 'H3';

            if (headerTag) {
                var header = document.createElement(headerTag);
                var inner = p.innerHTML;

                // Handle leading spaces
                var leadingSpaces = inner.match(/^((&nbsp;|\s)+)/i);
                if (leadingSpaces) {
                    var spaceCount = (leadingSpaces[0].match(/&nbsp;/g) || []).length;
                    var leftMargin = spaceCount * 7;
                    header.setAttribute('style', 'margin-left: ' + leftMargin + 'px');
                    inner = inner.replace(/^((&nbsp;|\s)+)/i, '');
                }

                header.innerHTML = inner;

                var keepStyle = style.match(/(margin-left|text-indent)\s*:\s*[^;]+/gi);
                if (keepStyle) {
                    keepStyle = keepStyle.filter(function (s) {
                        return !/-\d/.test(s);
                    });
                    if (keepStyle.length > 0) {
                        header.setAttribute('style', keepStyle.join('; '));
                    }
                }

                p.parentNode.replaceChild(header, p);
                return;
            }
        }

        // Clean paragraph styles
        var cleanStyle = style.replace(/(font-weight|font-size)\s*:\s*[^;]+;?/gi, '').trim();
        if (cleanStyle && cleanStyle !== ';') {
            p.setAttribute('style', cleanStyle);
        } else {
            p.removeAttribute('style');
        }
    });

    return tempDiv.innerHTML.trim();
};

// Helper function to process lists in DOM
function processListsInDOM(container) {
    var paragraphs = Array.from(container.querySelectorAll('p'));
    var currentList = null;
    var currentListType = null;
    var lastProcessedIndex = -1;

    paragraphs.forEach(function (p, index) {
        var text = p.textContent.trim();
        var style = p.getAttribute('style') || '';

        // Enhanced list detection regex
        var listMatch = text.match(/^(\(?[a-z]\)|[a-z]\.|[ivxlcdm]+\)|[IVXLCDM]+\)|\d+[.)]|[\u2022•\-–●])\s+/i);
        var listType = listMatch ? listMatch[1] : null;

        if (listType) {
            var type;
            var normalizedType = listType.toLowerCase().replace(/[()]/g, '');

            if (/^\d/.test(normalizedType)) type = 'decimal';
            else if (/^[a-z]/.test(normalizedType)) type = 'lower-alpha';
            else if (/^[A-Z]/.test(listType)) type = 'upper-alpha';
            else if (/^[ivxlcdm]+$/i.test(normalizedType)) {
                type = /[A-Z]/.test(listType) ? 'upper-roman' : 'lower-roman';
            } else type = 'disc';

            // Check if we should continue current list or start new one
            // For numbered lists, also check if there's a gap in indices
            var shouldStartNewList = false;

            if (!currentList || currentListType !== type) {
                shouldStartNewList = true;
            } else if (type === 'decimal') {
                // For numbered lists, check if the current number follows the sequence
                var currentNum = parseInt(listType);
                var expectedNum = lastProcessedIndex + 1;
                if (currentNum !== expectedNum) {
                    shouldStartNewList = true;
                }
            }

            if (shouldStartNewList) {
                currentList = document.createElement(type === 'disc' ? 'ul' : 'ol');
                currentList.style.listStyleType = type;
                currentListType = type;
                lastProcessedIndex = 0; // Reset for new list
                p.parentNode.insertBefore(currentList, p);
            }

            // Create list item
            var li = document.createElement('li');
            li.innerHTML = p.innerHTML.replace(listMatch[0], '').trim();

            // Preserve indentation
            var indentMatch = style.match(/margin-left\s*:\s*([^;]+)/i);
            if (indentMatch) {
                li.style.marginLeft = indentMatch[1];
            }

            currentList.appendChild(li);

            // Update last processed index for numbered lists
            if (type === 'decimal') {
                lastProcessedIndex = parseInt(listType);
            } else {
                lastProcessedIndex++;
            }

            p.remove();
        } else {
            // Reset list tracking for non-list items
            currentList = null;
            currentListType = null;
            lastProcessedIndex = -1;
        }
    });
}


const CustomEditor = ({value, onChange, containerProps = {}}) => {
        const editorRef = useRef(null);
        const savedSelection = useRef(null);
        const isFocused = useRef(false);
        const [fontSize, setFontSize] = useState(3);
        const [fontFamily, setFontFamily] = useState('Arial');
        const [formatBlock, setFormatBlock] = useState('p');

        useEffect(() => {
            const editor = editorRef.current;
            if (!editor) return;
            if (isFocused.current) return; // safer than activeElement checks
            if (value !== editor.innerHTML) {
                editor.innerHTML = value;
            }
        }, [value]);

        useEffect(function () {
            var editor = editorRef.current;
            if (!editor) return;

            var onSel = function () {
                // only if the selection is inside the editor
                var sel = window.getSelection();
                if (!sel || !sel.rangeCount) return;
                var container = sel.getRangeAt(0).commonAncestorContainer;
                if (editor.contains(container)) {
                    syncFontSizeUI();
                }
            };

            document.addEventListener('selectionchange', onSel);
            editor.addEventListener('keyup', onSel);
            editor.addEventListener('mouseup', onSel);

            return function () {
                document.removeEventListener('selectionchange', onSel);
                if (editor) {
                    editor.removeEventListener('keyup', onSel);
                    editor.removeEventListener('mouseup', onSel);
                }
            };
        }, []);


        const saveSelection = () => {
            const sel = window.getSelection();
            if (sel.rangeCount > 0) {
                savedSelection.current = sel.getRangeAt(0).cloneRange();
            }
        };

        const restoreSelection = () => {
            const r = savedSelection.current;
            if (r instanceof Range) {
                const editor = editorRef.current;
                if (editor && document.activeElement !== editor) editor.focus({preventScroll: true});
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(r);
            }
        };

        const handleInput = () => {
            const editor = editorRef.current;
            purgeZeroWidth(editor);
            const html = editor.innerHTML;
            Promise.resolve().then(() => {
                if (onChange) onChange({target: {value: html}});
                renumberAllAlphaRepeat(editor);
                saveSelection();
            });
        };


        const execWithSelection = (cmd, val = null) => {
            restoreSelection();
            exec(cmd, val);
            handleInput();
        };

        /*const handleFontSizeChange = (e) => {
            const size = e.target.value;
            setFontSize(size);
            execWithSelection('fontSize', size);
        };*/

        var fontSizeMap = fontSizeMap || {
            1: '10px', 2: '13px', 3: '16px', 4: '18px', 5: '24px', 6: '32px', 7: '48px'
        };
        var BLOCK_SIZABLE = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'LI', 'DIV', 'TD', 'TH']);


        const handleFontSizeChange = function (e) {
            var key = String(e.target.value);                 // "1".."7"
            var cssSize = fontSizeMap[key] || '16px';
            setFontSize(key);                                 // keep the input in sync

            restoreSelection();
            var sel = window.getSelection();
            var editor = editorRef.current;
            if (!sel || !sel.rangeCount || !editor) return;

            // Freeze scroll (window + editor) so reflow/selection restore can't jump
            var freeze = beginScrollFreeze(editor);

            // Use the LIVE range (mutations will adjust it automatically)
            var range = sel.getRangeAt(0);
            if (!editor.contains(range.commonAncestorContainer)) {
                freeze.restore();
                return;
            }

            var desiredRange;

            // ---------- APPLY SIZE ----------
            if (sel.isCollapsed) {
                // collapsed caret → set on nearest sizable (inline or block)
                var target = nearestSizableAncestor(range.startContainer, editor);
                if (target) {
                    // Beat any author CSS with !important if needed
                    if (target.style && typeof target.style.setProperty === 'function') {
                        target.style.setProperty('font-size', cssSize, 'important');
                    } else {
                        target.style.fontSize = cssSize;
                    }
                    mergeAdjacentSizeSpans(target.parentNode || editor);

                    // keep caret exactly where it was
                    desiredRange = document.createRange();
                    desiredRange.setStart(range.startContainer, range.startOffset);
                    desiredRange.collapse(true);
                } else {
                    // no suitable ancestor: create a span to type into
                    var span = document.createElement('span');
                    span.style.fontSize = cssSize;
                    var anchor = document.createTextNode('');
                    span.appendChild(anchor);
                    range.insertNode(span);

                    desiredRange = document.createRange();
                    desiredRange.setStart(anchor, 0);
                    desiredRange.collapse(true);
                }
            } else {
                const bm = placeSelectionBookmarks(sel);

                // Now work with the live range safely
                const doc = range.commonAncestorContainer.ownerDocument || document;

                // 1) style intersecting blocks
                const walker = doc.createTreeWalker(
                    range.commonAncestorContainer,
                    NodeFilter.SHOW_ELEMENT,
                    {
                        acceptNode: function (el) {
                            if (!el || !BLOCK_SIZABLE.has(el.tagName)) return NodeFilter.FILTER_SKIP;
                            const r = doc.createRange();
                            r.selectNode(el);
                            const intersects = range.compareBoundaryPoints(Range.END_TO_END, r) > 0 &&
                                range.compareBoundaryPoints(Range.START_TO_START, r) < 0;
                            if (typeof r.detach === 'function') r.detach();
                            return intersects ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
                        }
                    }
                );
                const touchedBlocks = [];
                while (walker.nextNode()) touchedBlocks.push(walker.currentNode);
                touchedBlocks.forEach(function (el) {
                    if (el.style && typeof el.style.setProperty === 'function') {
                        el.style.setProperty('font-size', cssSize, 'important');
                    } else {
                        el.style.fontSize = cssSize;
                    }
                });

                // 2) wrap remaining naked text slices
                const applied = wrapSelectionWithSpan(range, cssSize);
                mergeAdjacentSizeSpans((applied && applied.parentNode) || editor);

                // Rebuild EXACT original selection from bookmarks (prevents “caret at end” bug)
                const rebuilt = rangeFromBookmarks(bm);
                removeBookmarks(bm);

                desiredRange = rebuilt || (function () {
                    // Fallback: keep current live range if something went odd
                    const live = sel.getRangeAt(0);
                    const r = document.createRange();
                    r.setStart(live.startContainer, live.startOffset);
                    r.setEnd(live.endContainer, live.endOffset);
                    return r;
                })();
            }

            // --------------------------------

            // Restore selection and unfreeze scroll after DOM settles
            setTimeout(function () {
                var s = window.getSelection();
                s.removeAllRanges();
                if (desiredRange) s.addRange(desiredRange);
                if (typeof savedSelection !== 'undefined' && savedSelection && typeof savedSelection.current !== 'undefined') {
                    savedSelection.current = desiredRange; // keep your saved selection aligned
                }

                freeze.restore();

                if (typeof syncFontSizeUI === 'function') syncFontSizeUI();
                setTimeout(function () {
                    handleInput();
                }, 0);
            }, 0);
        };


        function nearestSizableAncestor(node, root) {
            var cur = node && (node.nodeType === Node.TEXT_NODE ? node.parentNode : node);
            while (cur && cur !== root) {
                if (cur.nodeType === 1) {
                    if (isInlineElement(cur) && hasFontSize(cur)) return cur; // prefer existing inline
                    if (BLOCK_SIZABLE.has(cur.tagName)) return cur;           // allow blocks to carry size
                    if (isInlineElement(cur)) return cur;                     // inline without size (we can set)
                }
                cur = cur.parentNode;
            }
            return null;
        }

        function beginScrollFreeze(editor) {
            var state = {
                wx: window.scrollX || window.pageXOffset,
                wy: window.scrollY || window.pageYOffset,
                ex: editor.scrollLeft,
                ey: editor.scrollTop,
                prevBehavior: editor.style.scrollBehavior
            };
            // disable any smooth scrolling while we restore
            editor.style.scrollBehavior = 'auto';

            function restoreNow() {
                editor.scrollLeft = state.ex;
                editor.scrollTop = state.ey;
                window.scrollTo(state.wx, state.wy);
            }

            return {
                restore: function () {
                    // do it now, then again next frame, then once after microtask — beats React/layout
                    restoreNow();
                    requestAnimationFrame(restoreNow);
                    setTimeout(function () {
                        restoreNow();
                        editor.style.scrollBehavior = state.prevBehavior || '';
                    }, 0);
                }
            };
        }


        function syncFontSizeUI() {
            var editor = editorRef.current;
            var sel = window.getSelection();
            if (!editor || !sel || !sel.rangeCount) return;

            var range = sel.getRangeAt(0);

            function norm(px) {
                if (!px) return '';
                var n = parseFloat(String(px).replace('px', ''));
                return isNaN(n) ? '' : Math.round(n) + 'px';
            }

            function getSizeFromEl(el) {
                if (!el) return '';
                if (el.nodeType === 3) el = el.parentNode;
                if (el && el.style && el.style.fontSize) return norm(el.style.fontSize);
                var cs = window.getComputedStyle(el, null);
                return norm(cs && cs.fontSize);
            }

            var size = '';

            if (sel.isCollapsed) {
                size = getSizeFromEl(range.startContainer);
            } else {
                var doc = range.commonAncestorContainer.ownerDocument || document;
                var walker = doc.createTreeWalker(
                    range.commonAncestorContainer,
                    NodeFilter.SHOW_TEXT,
                    {
                        acceptNode: function (node) {
                            if (!node.data || !node.data.trim()) return NodeFilter.FILTER_REJECT;
                            var r = doc.createRange();
                            r.selectNodeContents(node);
                            var inter = range.compareBoundaryPoints(Range.END_TO_END, r) > 0 &&
                                range.compareBoundaryPoints(Range.START_TO_START, r) < 0;
                            if (typeof r.detach === 'function') r.detach();
                            return inter ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
                        }
                    }
                );
                var weights = {};
                var best = '';
                var bestW = -1;
                while (walker.nextNode()) {
                    var node = walker.currentNode;
                    var start = (range.startContainer === node) ? range.startOffset : 0;
                    var end = (range.endContainer === node) ? range.endOffset : node.data.length;
                    if (start >= end) continue;
                    var w = end - start;
                    var s = getSizeFromEl(node);
                    if (!s) continue;
                    weights[s] = (weights[s] || 0) + w;
                    if (weights[s] > bestW) {
                        bestW = weights[s];
                        best = s;
                    }
                }
                size = best;
            }

            if (!size) return; // nothing detectable; keep current dropdown

            // map back to your keys; exact match or nearest
            var rev = {};
            Object.keys(fontSizeMap).forEach(function (k) {
                rev[String(fontSizeMap[k]).trim().toLowerCase()] = k;
            });

            if (rev[size]) {
                setFontSize(rev[size]);
            } else {
                var target = parseFloat(size);
                var nearestKey = null, diff = Infinity;
                Object.keys(fontSizeMap).forEach(function (k) {
                    var v = parseFloat(String(fontSizeMap[k]).replace('px', ''));
                    var d = Math.abs(v - target);
                    if (!isNaN(d) && d < diff) {
                        diff = d;
                        nearestKey = k;
                    }
                });
                if (nearestKey) setFontSize(nearestKey);
            }
        }


        function pxToNumber(px) {
            if (!px) return 0;
            var n = parseFloat(String(px).replace('px', ''));
            return isNaN(n) ? 0 : n;
        }

        function nearestKeyForPx(px) {
            // pick the key whose mapped px is closest
            var bestKey = '3', bestDiff = Infinity, target = pxToNumber(px);
            for (var k in fontSizeMap) {
                if (!fontSizeMap.hasOwnProperty(k)) continue;
                var diff = Math.abs(pxToNumber(fontSizeMap[k]) - target);
                if (diff < bestDiff) {
                    bestDiff = diff;
                    bestKey = k;
                }
            }
            return bestKey;
        }

        function getComputedFontSizeAtCaret(editor) {
            var sel = window.getSelection();
            if (!sel || !sel.rangeCount) return '';
            var node = sel.getRangeAt(0).startContainer;
            var el = node.nodeType === 1 ? node : node.parentElement;
            while (el && el !== editor && el.nodeType === 1) {
                // prefer explicitly styled inline first
                if (el.style && el.style.fontSize) return el.style.fontSize;
                el = el.parentElement;
            }
            // fallback: computed style of actual element at caret
            var anchorEl = node.nodeType === 1 ? node : node.parentElement;
            if (!anchorEl || !editor.contains(anchorEl)) return '';
            var cs = window.getComputedStyle(anchorEl);
            return cs && cs.fontSize ? cs.fontSize : '';
        }


        const INLINE_TAGS = new Set([
            'SPAN', 'B', 'I', 'U', 'EM', 'STRONG', 'A', 'SMALL', 'SUB', 'SUP', 'CODE', 'MARK'
        ]);

        function placeSelectionBookmarks(sel) {
            const range = sel.getRangeAt(0);

            const startMarker = document.createElement('span');
            startMarker.setAttribute('data-bm', 'start');
            startMarker.style.display = 'inline';
            startMarker.style.fontSize = '0';
            startMarker.style.lineHeight = '0';
            startMarker.style.opacity = '0';

            const endMarker = document.createElement('span');
            endMarker.setAttribute('data-bm', 'end');
            endMarker.style.display = 'inline';
            endMarker.style.fontSize = '0';
            endMarker.style.lineHeight = '0';
            endMarker.style.opacity = '0';

            // insert end first (doesn't disturb start offsets)
            const rEnd = range.cloneRange();
            rEnd.collapse(false);
            rEnd.insertNode(endMarker);

            const rStart = range.cloneRange();
            rStart.collapse(true);
            rStart.insertNode(startMarker);

            return {startMarker, endMarker};
        }

        function rangeFromBookmarks(bm) {
            const {startMarker, endMarker} = bm;
            const r = document.createRange();
            if (startMarker && startMarker.parentNode && endMarker && endMarker.parentNode) {
                r.setStartAfter(startMarker);
                r.setEndBefore(endMarker);
            } else if (startMarker && startMarker.parentNode) {
                r.setStartAfter(startMarker);
                r.collapse(true);
            } else if (endMarker && endMarker.parentNode) {
                r.setStartBefore(endMarker);
                r.collapse(true);
            } else {
                return null;
            }
            return r;
        }

        function removeBookmarks(bm) {
            ['startMarker', 'endMarker'].forEach(k => {
                const n = bm[k];
                if (n && n.parentNode) n.parentNode.removeChild(n);
            });
        }


        var fontSizeReverse = (function buildReverse(map) {
            var rev = {};
            Object.keys(map).forEach(function (k) {
                rev[String(map[k]).trim().toLowerCase()] = k;
            });
            return rev;
        })(fontSizeMap);

        function getSelectionEffectiveSize(editor) {
            var sel = window.getSelection();
            if (!sel || !sel.rangeCount) return '';

            var range = sel.getRangeAt(0);

            // Collapsed caret → just read from nearest element (inline or block)
            if (sel.isCollapsed) {
                var el = nearestElement(range.startContainer);
                // Prefer inline style if present, else computed
                if (el && el.style && el.style.fontSize) return normalizePx(el.style.fontSize);
                return getComputedPx(el || editor);
            }

            // Selection → check if all text shares same effective size
            var doc = range.commonAncestorContainer.ownerDocument || document;
            var walker = doc.createTreeWalker(
                range.commonAncestorContainer,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: function (node) {
                        if (!node.data || !node.data.trim()) return NodeFilter.FILTER_REJECT;
                        var r = doc.createRange();
                        r.selectNodeContents(node);
                        var intersects =
                            range.compareBoundaryPoints(Range.END_TO_END, r) > 0 &&
                            range.compareBoundaryPoints(Range.START_TO_START, r) < 0;
                        if (typeof r.detach === 'function') r.detach();
                        return intersects ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
                    }
                }
            );

            var sizes = {};
            while (walker.nextNode()) {
                var el = nearestElement(walker.currentNode);
                var size = (el && el.style && el.style.fontSize) ? normalizePx(el.style.fontSize) : getComputedPx(el);
                if (!size) continue;
                sizes[size] = true;
                // early exit if mixed
                if (Object.keys(sizes).length > 1) return '';
            }
            return Object.keys(sizes)[0] || '';
        }


        function isInlineElement(el) {
            return el && el.nodeType === 1 && INLINE_TAGS.has(el.tagName);
        }

        function normalizePx(px) {
            if (!px) return '';
            var n = parseFloat(String(px).replace('px', '').trim());
            if (isNaN(n)) return '';
            return Math.round(n) + 'px';
        }

        function nearestElement(node) {
            return node && node.nodeType === Node.TEXT_NODE ? node.parentNode : node;
        }

        function getComputedPx(el) {
            if (!el || el.nodeType !== 1) return '';
            var cs = window.getComputedStyle(el, null);
            return normalizePx(cs && cs.getPropertyValue('font-size'));
        }


        function hasFontSize(el) {
            if (!el || el.nodeType !== 1) return false;
            const fs = el.style && el.style.fontSize;
            return !!fs && fs.trim() !== '';
        }

        function sameFontSize(a, b) {
            if (!a || !b) return false;
            var af = (a.style && a.style.fontSize ? a.style.fontSize : '').trim();
            var bf = (b.style && b.style.fontSize ? b.style.fontSize : '').trim();
            return af === bf;
        }

        function isStyleSpan(node) {
            return node && node.nodeType === 1 && node.tagName === 'SPAN';
        }

        function isInlineFormattingTag(node) {
            if (!node || node.nodeType !== 1) return false;
            var t = node.tagName;
            // add more inline tags if you use them
            return t === 'SPAN' || t === 'B' || t === 'STRONG' || t === 'I' || t === 'EM' || t === 'U' || t === 'A' || t === 'CODE' || t === 'SUB' || t === 'SUP';
        }

        function sameSize(a, b) {
            return isStyleSpan(a) && isStyleSpan(b) && (a.style.fontSize || '') === (b.style.fontSize || '');
        }

        function mergeAdjacentSizeSpans(root) {
            if (!root) return;

            // Walk all spans with font-size under root
            const spans = root.querySelectorAll('span[style*="font-size"]');
            spans.forEach((span) => {
                // First: unwrap redundant nesting
                unwrapIfRedundant(span);

                // Merge with immediately adjacent previous sibling if same size
                let prev = span.previousSibling;
                if (prev && prev.nodeType === 1 && prev.tagName === 'SPAN' && sameFontSize(prev, span)) {
                    while (span.firstChild) prev.appendChild(span.firstChild);
                    span.remove();
                    span = prev; // continue merging using the merged node
                }

                // Merge with immediately adjacent next sibling if same size
                let next = span.nextSibling;
                if (next && next.nodeType === 1 && next.tagName === 'SPAN' && sameFontSize(next, span)) {
                    while (next.firstChild) span.appendChild(next.firstChild);
                    next.remove();
                }

                // After merges, check redundancy again
                unwrapIfRedundant(span);
            });
        }

        function wrapSelectionWithSpan(range, cssSize) {
            if (range.collapsed) return null;

            var doc = range.commonAncestorContainer.ownerDocument || document;
            var walker = doc.createTreeWalker(
                range.commonAncestorContainer,
                NodeFilter.SHOW_TEXT,
                {
                    acceptNode: function (node) {
                        if (!node.data || !node.data.trim()) return NodeFilter.FILTER_REJECT;

                        var r = doc.createRange();
                        r.selectNodeContents(node);

                        var intersects =
                            range.compareBoundaryPoints(Range.END_TO_END, r) > 0 &&
                            range.compareBoundaryPoints(Range.START_TO_START, r) < 0;

                        // no optional chaining here
                        if (typeof r.detach === 'function') r.detach();

                        return intersects ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
                    }
                }
            );

            var lastAppliedSpan = null;
            var toProcess = [];
            while (walker.nextNode()) toProcess.push(walker.currentNode);

            toProcess.forEach(function (textNode) {
                var localRange = doc.createRange();
                localRange.selectNodeContents(textNode);

                var start = (range.startContainer === textNode) ? range.startOffset : 0;
                var end = (range.endContainer === textNode) ? range.endOffset : textNode.data.length;

                if (start >= end) {
                    if (typeof localRange.detach === 'function') localRange.detach();
                    return;
                }

                var inline = nearestInlineAncestor(textNode, range.commonAncestorContainer);
                if (inline && hasFontSize(inline)) {
                    inline.style.fontSize = cssSize;
                    lastAppliedSpan = inline;
                    if (typeof localRange.detach === 'function') localRange.detach();
                    return;
                }

                var before = start > 0 ? textNode.splitText(start) : textNode;
                var needSplit = end < before.data.length;
                var sliceAfter = needSplit ? before.splitText(end - 0) : null;
                var target = before; // 'before' is our selected slice

                var span = doc.createElement('span');
                span.style.fontSize = cssSize;
                target.parentNode.insertBefore(span, target);
                span.appendChild(target);

                lastAppliedSpan = span;

                if (typeof localRange.detach === 'function') localRange.detach();
            });

            return lastAppliedSpan;
        }

        function nearestInlineAncestor(node, root) {
            let cur = node && (node.nodeType === Node.TEXT_NODE ? node.parentNode : node);
            while (cur && cur !== root) {
                if (isInlineElement(cur)) return cur;
                cur = cur.parentNode;
            }
            return null;
        }

        function unwrapIfRedundant(span) {
            if (!span || span.nodeType !== 1) return;
            const parent = span.parentNode;
            if (!parent || parent.nodeType !== 1) return;

            // If parent has same font-size, lift children up and remove span
            if (sameFontSize(span, parent)) {
                while (span.firstChild) parent.insertBefore(span.firstChild, span);
                parent.removeChild(span);
            }
        }


        const handleFontFamilyChange = (e) => {
            const family = e.target.value;
            setFontFamily(family);
            restoreSelection();
            exec('fontName', family);
            handleInput();
        };

        const handleFormatBlockChange = (e) => {
            const block = e.target.value;
            setFormatBlock(block);
            restoreSelection();
            exec('formatBlock', block);
            handleInput();
        };

        const applyListStyle = (style, type) => {
            restoreSelection();
            const selection = window.getSelection();
            if (!selection.rangeCount) return;

            let node = selection.anchorNode;

            // Traverse up to the LI element
            while (node && node.nodeName !== 'LI') {
                node = node.parentNode;
            }

            if (node && node.nodeName === 'LI') {
                // 🧠 We're inside an <li> → Create nested list manually
                let nestedList = Array.from(node.childNodes).find(
                    (child) => child.nodeName === 'UL' || child.nodeName === 'OL'
                );

                if (!nestedList) {
                    nestedList = document.createElement(type === 'insertOrderedList' ? 'OL' : 'UL');
                    nestedList.style.listStyleType = style;
                    nestedList.style.paddingLeft = '20px';
                    node.appendChild(nestedList);
                }

                const newItem = document.createElement('LI');
                newItem.innerHTML = '&nbsp;';
                nestedList.appendChild(newItem);

                // Set cursor inside the new nested <li>
                const range = document.createRange();
                range.setStart(newItem, 0);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);

                handleInput();
            } else {
                // ✍️ Not inside a list item → use execCommand to start a new list
                exec(type);
                const newSelection = window.getSelection();
                let listNode = newSelection.anchorNode;
                while (listNode && listNode.nodeName !== 'UL' && listNode.nodeName !== 'OL') {
                    listNode = listNode.parentNode;
                }
                if (listNode) {
                    listNode.style.listStyleType = style;
                    listNode.style.paddingLeft = '20px';
                }

                handleInput();
            }
        };

        /*const forceStartListAtCursor = (style, type) => {
            restoreSelection();

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            if (!range) return;

            const selectedText = range.toString().trim();
            const selectedContent = range.cloneContents();
            const existingLists = selectedContent.querySelectorAll('ul, ol');
            const existingListItems = selectedContent.querySelectorAll('li');

            // --- CASE 1: Convert existing list types ---
            if (existingLists.length > 0 || existingListItems.length > 0) {
                const container = range.commonAncestorContainer;
                const containerElement = container.nodeType === 1 ? container : container.parentElement;
                const listsInSelection = [];

                if (containerElement) {
                    const allLists = containerElement.querySelectorAll('ul, ol');
                    allLists.forEach(list => {
                        if (selection.containsNode(list, true)) {
                            listsInSelection.push(list);
                        }
                    });
                }

                listsInSelection.forEach(list => {
                    const newListType = type === 'insertOrderedList' ? 'ol' : 'ul';
                    if (list.tagName.toLowerCase() !== newListType) {
                        const newList = document.createElement(newListType);
                        newList.style.listStyleType = style;
                        newList.style.listStylePosition = 'outside';
                        newList.style.paddingLeft = '2rem';
                        newList.style.marginLeft = '0';

                        while (list.firstChild) {
                            newList.appendChild(list.firstChild);
                        }

                        list.parentNode.replaceChild(newList, list);
                    } else {
                        list.style.listStyleType = style;
                        list.style.listStylePosition = 'outside';
                        list.style.paddingLeft = '2rem';
                        list.style.marginLeft = '0';
                    }
                });

                handleInput();
                return;
            }

            // --- CASE 2: Selection contains text, create a new list ---
            if (selectedText) {
                const selectedHTML = range.cloneContents();
                const tempDiv = document.createElement('div');
                tempDiv.appendChild(selectedHTML);

                const list = document.createElement(type === 'insertOrderedList' ? 'OL' : 'UL');
                list.style.listStyleType = style;
                list.style.listStylePosition = 'outside';
                list.style.paddingLeft = '2rem';
                list.style.marginLeft = '0';

                // Flatten structure — remove block-level elements inside the selected content
                const contentContainer = document.createElement('div');
                contentContainer.innerHTML = tempDiv.innerHTML;

                contentContainer.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach(el => {
                    const span = document.createElement('span');
                    span.innerHTML = el.innerHTML;
                    el.replaceWith(span);
                });

                contentContainer.querySelectorAll('*').forEach(el => {
                    el.style.margin = '';
                    el.style.padding = '';
                    el.style.textIndent = '';
                });

                const li = document.createElement('LI');
                li.innerHTML = contentContainer.innerHTML.trim() || selectedText;
                li.style.margin = '0';
                li.style.padding = '0';
                li.style.textIndent = '0';
                li.style.display = 'list-item'; // critical to preserve proper layout

                list.appendChild(li);

                // Replace selected content with the list
                range.deleteContents();
                range.insertNode(list);

                // Move cursor inside the new list item
                const newRange = document.createRange();
                newRange.selectNodeContents(li);
                newRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(newRange);

                handleInput();
                return;
            }

            // --- CASE 3: No selection, just insert new empty list ---
            const rect = range.getBoundingClientRect();
            const editorRect = editorRef.current.getBoundingClientRect();
            const relativeIndent = Math.max(rect.left - editorRect.left, 0);

            const list = document.createElement(type === 'insertOrderedList' ? 'OL' : 'UL');
            list.style.listStyleType = style;
            list.style.listStylePosition = 'outside';
            list.style.paddingLeft = '2rem';
            list.style.marginLeft = `${relativeIndent}px`;

            const li = document.createElement('LI');
            li.innerHTML = '&nbsp;';
            li.style.margin = '0';
            li.style.padding = '0';
            li.style.textIndent = '0';
            li.style.display = 'list-item';

            list.appendChild(li);

            range.deleteContents();
            range.insertNode(list);

            const newRange = document.createRange();
            newRange.selectNodeContents(li);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);

            handleInput();
        };*/

        const forceStartListAtCursor = (style, type) => {
            restoreSelection();

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            if (!range) return;

            const editor = editorRef.current;

            // ===== CASE 0: If caret/selection is inside a list, update ONLY that innermost list =====
            // ===== CASE 0: Caret/selection is inside a list? =====
            const listAtCaret =
                getNearestList(range.startContainer, editor) ||
                getNearestList(range.commonAncestorContainer, editor);

            if (listAtCaret) {
                const wantTag = (type === 'insertOrderedList') ? 'OL' : 'UL';

                // Find current LI for caret restore
                const activeLi = closestLi(range.startContainer, editor) || listAtCaret.querySelector('li');

                if (selection.isCollapsed && activeLi) {
                    // ===== CASE 0A: collapsed caret inside a list -> create a NEW NESTED list here =====
                    const subList = document.createElement(wantTag);
                    if (style === 'lower-alpha-repeat') {
                        ensureAlphaRepeat(subList);
                    } else {
                        subList.style.listStyleType = style;
                    }

                    const subLi = document.createElement('LI');
                    subLi.innerHTML = '&nbsp;';
                    subList.appendChild(subLi);

                    // Insert nested list exactly at caret (Range will split text nodes if needed)
                    range.insertNode(subList);

                    // Place caret inside the new sub list item
                    const r = document.createRange();
                    r.selectNodeContents(subLi);
                    r.collapse(true);
                    selection.removeAllRanges();
                    selection.addRange(r);

                    handleInput();
                    return; // ✅ we created a sub-list, don't update parent style
                } else {
                    // ===== CASE 0B: selection spans or touches a list -> update THAT list only =====
                    let target = listAtCaret;
                    if (listAtCaret.tagName !== wantTag) {
                        target = replaceListTagPreserve(listAtCaret, wantTag); // UL<->OL, keep indent/styles
                    }
                    setMarkerOnly(target, style); // only change marker; keep existing indentation

                    // Restore caret at end of active LI if we had one
                    if (activeLi) {
                        const caretRange = document.createRange();
                        caretRange.selectNodeContents(activeLi);
                        caretRange.collapse(false);
                        selection.removeAllRanges();
                        selection.addRange(caretRange);
                    }

                    handleInput();
                    return; // ✅ done — no new list created
                }
            }
            // ===== END CASE 0 =====

            const selectedText = range.toString().trim();
            const selectedContent = range.cloneContents();
            const existingLists = selectedContent.querySelectorAll('ul, ol');
            const existingListItems = selectedContent.querySelectorAll('li');

            // ===== CASE 1: Selection covers lists — update only lists fully inside (deepest only) =====
            if (existingLists.length > 0 || existingListItems.length > 0) {
                const container = range.commonAncestorContainer;
                const containerElement = container.nodeType === 1 ? container : container.parentElement;

                let listsInSelection = [];
                if (containerElement) {
                    containerElement.querySelectorAll('ul, ol').forEach(list => {
                        // full containment ONLY (avoid matching parent/ancestor lists)
                        if (selection.containsNode(list, false)) listsInSelection.push(list);
                    });
                }

                // keep only deepest lists (exclude ancestors that contain another selected list)
                const deepest = listsInSelection.filter(l =>
                    !listsInSelection.some(other => other !== l && other.contains(l))
                );

                deepest.forEach(list => {
                    const wantTag = (type === 'insertOrderedList') ? 'OL' : 'UL';
                    let target = list;
                    if (list.tagName !== wantTag) {
                        target = replaceListTagPreserve(list, wantTag); // keep indent/attrs
                    }
                    setMarkerOnly(target, style); // just marker type, no indent change
                });

                handleInput();
                return;
            }
            // ===== END CASE 1 =====

            // --- CASE 2: Selection contains text, create a new list (your original logic) ---
            if (selectedText) {
                const selectedHTML = range.cloneContents();
                const tempDiv = document.createElement('div');
                tempDiv.appendChild(selectedHTML);

                const list = document.createElement(type === 'insertOrderedList' ? 'OL' : 'UL');
                list.style.listStyleType = style;
                list.style.listStylePosition = 'outside';
                list.style.paddingLeft = '2rem';
                list.style.marginLeft = '0';

                const contentContainer = document.createElement('div');
                contentContainer.innerHTML = tempDiv.innerHTML;

                contentContainer.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach(el => {
                    const span = document.createElement('span');
                    span.innerHTML = el.innerHTML;
                    el.replaceWith(span);
                });
                contentContainer.querySelectorAll('*').forEach(el => {
                    el.style.margin = '';
                    el.style.padding = '';
                    el.style.textIndent = '';
                });

                const li = document.createElement('LI');
                li.innerHTML = contentContainer.innerHTML.trim() || selectedText;
                li.style.margin = '0';
                li.style.padding = '0';
                li.style.textIndent = '0';
                li.style.display = 'list-item';
                list.appendChild(li);

                range.deleteContents();
                range.insertNode(list);

                const newRange = document.createRange();
                newRange.selectNodeContents(li);
                newRange.collapse(false);
                selection.removeAllRanges();
                selection.addRange(newRange);

                handleInput();
                return;
            }

            // --- CASE 3: No selection, insert a new empty list at caret (your original logic) ---
            const rect = range.getBoundingClientRect();
            const editorRect = editor.getBoundingClientRect();
            const relativeIndent = Math.max(rect.left - editorRect.left, 0);

            const list = document.createElement(type === 'insertOrderedList' ? 'OL' : 'UL');
            if (style === 'lower-alpha-repeat') {
                ensureAlphaRepeat(list);
            } else {
                list.style.listStyleType = style;
                list.style.listStylePosition = 'outside';
            }
            list.style.paddingLeft = '2rem';
            list.style.marginLeft = `${relativeIndent}px`;

            const li = document.createElement('LI');
            li.innerHTML = '&nbsp;';
            li.style.margin = '0';
            li.style.padding = '0';
            li.style.textIndent = '0';
            li.style.display = 'list-item';
            list.appendChild(li);

            range.deleteContents();
            range.insertNode(list);

            const newRange2 = document.createRange();
            newRange2.selectNodeContents(li);
            newRange2.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange2);

            handleInput();
        };

// ---- helpers (no optional chaining) ----
        const getNearestList = (node, editor) => {
            let el = node ? (node.nodeType === 1 ? node : node.parentElement) : null;
            while (el && el !== editor) {
                const tag = el.tagName;
                if (tag === 'UL' || tag === 'OL') return el; // innermost list
                el = el.parentElement;
            }
            return null;
        };

        const closestLi = (node, editor) => {
            let el = node ? (node.nodeType === 1 ? node : node.parentElement) : null;
            while (el && el !== editor) {
                if (el.tagName === 'LI') return el;
                el = el.parentElement;
            }
            return null;
        };

// change marker only, preserve indent/position
        const setMarkerOnly = (list, style) => {
            if (style === 'lower-alpha-repeat') {
                ensureAlphaRepeat(list);
                return;
            }
            disableAlphaRepeat(list);
            list.style.listStyleType = style; // don't touch padding/margins/indent
        };

// UL <-> OL replacement preserving attributes + inline styles + children
        const replaceListTagPreserve = (oldList, newTagName) => {
            const replacement = document.createElement(newTagName.toUpperCase());
            // copy attributes except style (we copy styles separately)
            const attrs = oldList.attributes;
            for (let i = 0; i < attrs.length; i++) {
                const a = attrs[i];
                if (a.name !== 'style') replacement.setAttribute(a.name, a.value);
            }
            // copy inline styles exactly (keeps existing indent/position)
            replacement.style.cssText = oldList.style.cssText;

            while (oldList.firstChild) replacement.appendChild(oldList.firstChild);
            oldList.replaceWith(replacement);
            return replacement;
        };


        const handlePaste = (e) => {
            e.preventDefault();
            const clipboardData = e.clipboardData || window.clipboardData;
            let pastedData = clipboardData.getData('text/html') || clipboardData.getData('text/plain');
            pastedData = pastedData.replace(/&#8203;|&ZeroWidthSpace;|[\u200B\u200C\u200D\uFEFF]/g, ''); // <-- add
            const cleanedHTML = cleanWordHTML(pastedData);
            restoreSelection && restoreSelection();
            exec('insertHTML', cleanedHTML);
            handleInput();
        };

        const adjustParagraphIndent = (delta) => {
            restoreSelection();
            const selection = window.getSelection();
            if (!selection.rangeCount || selection.isCollapsed) return;

            const range = selection.getRangeAt(0);
            const editor = editorRef.current;
            if (!editor.contains(range.commonAncestorContainer)) return;

            // 🔹 NEW: capture current range to restore later
            const beforeRange = range.cloneRange();

            var anchor = range.startContainer;
            var el = anchor && anchor.nodeType === 1 ? anchor : (anchor && anchor.parentElement);
            while (el && el !== editor && !(el.tagName === 'LI' || el.tagName === 'P' || el.tagName === 'DIV')) {
                el = el.parentElement;
            }

            // Lock which thing we’re allowed to touch this call
            var targetList = null;
            var targetBlock = null;
            if (el && el.tagName === 'LI') targetList = el.parentElement;        // only this OL/UL
            if (el && (el.tagName === 'P' || el.tagName === 'DIV')) targetBlock = el; // only this P/DIV

            var listMode = !!targetList;
            var blockMode = !!targetBlock && !listMode; // block wins only if not inside an LI

            const BLOCK_TAGS = ['LI', 'P', 'DIV', 'BLOCKQUOTE'];

            // Helper: Find block elements in range
            const getBlocksInRange = (range, editor, blockTags) => {
                const blocks = new Set();
                const treeWalker = document.createTreeWalker(
                    editor,
                    NodeFilter.SHOW_ELEMENT,
                    {
                        acceptNode: (node) =>
                            range.intersectsNode(node) && blockTags.includes(node.tagName)
                                ? NodeFilter.FILTER_ACCEPT
                                : NodeFilter.FILTER_SKIP,
                    }
                );
                let node = treeWalker.nextNode();
                while (node) {
                    blocks.add(node);
                    node = treeWalker.nextNode();
                }
                return blocks;
            };

            const blocks = getBlocksInRange(range, editor, BLOCK_TAGS);
            const updatedLists = new WeakSet();

            blocks.forEach(function (block) {
                var tag = block.tagName;

                // ---------- LIST MODE: only touch the active list ----------
                if (listMode) {
                    if (tag !== 'LI') return;
                    var parentList = block.parentElement;
                    if (!parentList || (parentList.tagName !== 'OL' && parentList.tagName !== 'UL')) return;
                    if (parentList !== targetList) return; // ✅ only the active list

                    if (!updatedLists.has(parentList)) {
                        // Read computed styles so we start from actual current positions
                        var cs = window.getComputedStyle(parentList);
                        var curPad = parseInt((cs.paddingLeft || '0').replace('px', ''), 10);
                        if (isNaN(curPad)) curPad = 0;
                        var curMar = parseInt((cs.marginLeft || '0').replace('px', ''), 10);
                        if (isNaN(curMar)) curMar = 0;

                        // Adjust both — padding keeps text gap, margin moves marker
                        var newPad = Math.max(0, curPad + delta);
                        var newMar = Math.max(0, curMar + delta);

                        parentList.style.paddingLeft = newPad + 'px';
                        parentList.style.setProperty('margin-left', newMar + 'px', 'important'); // ← makes the marker move
                        if (!parentList.style.listStylePosition) parentList.style.listStylePosition = 'outside';

                        updatedLists.add(parentList);
                    }

                    // Never style the LI itself
                    block.style.marginLeft = '';
                    block.style.paddingLeft = '';
                    return;
                }

                // ---------- BLOCK MODE: only touch the active paragraph/div ----------
                if (blockMode) {
                    if (!(tag === 'P' || tag === 'DIV')) return;
                    if (block !== targetBlock) return;                // ✅ only this one block

                    var currentIndent = parseInt(block.style.marginLeft || '0', 10);
                    if (isNaN(currentIndent)) {
                        var computedMargin = window.getComputedStyle(block).marginLeft;
                        currentIndent = parseInt((computedMargin || '0').replace('px', ''), 10);
                        if (isNaN(currentIndent)) currentIndent = 0;
                    }
                    // Allow negatives for block-level elements
                    var newIndent = currentIndent + delta;
                    block.style.marginLeft = newIndent + 'px';
                    return;
                }

                // ---------- Fallback (no active LI/P/DIV found): keep your original behavior ----------
                if (tag === 'LI') {
                    var parentList2 = block.parentElement;
                    if ((parentList2.tagName === 'OL' || parentList2.tagName === 'UL') && !updatedLists.has(parentList2)) {
                        var computed2 = window.getComputedStyle(parentList2).paddingLeft;
                        var cur2 = parseInt((computed2 || '0').replace('px', ''), 10);
                        if (isNaN(cur2)) cur2 = 0;
                        var newPad2 = Math.max(0, cur2 + delta);
                        parentList2.style.paddingLeft = newPad2 + 'px';
                        parentList2.style.listStylePosition = 'outside';
                        updatedLists.add(parentList2);
                    }
                    block.style.marginLeft = '';
                    block.style.paddingLeft = '';
                } else if (tag === 'P' || tag === 'DIV') {
                    var curB = parseInt(block.style.marginLeft || '0', 10);
                    if (isNaN(curB)) {
                        var cm2 = window.getComputedStyle(block).marginLeft;
                        curB = parseInt((cm2 || '0').replace('px', ''), 10);
                        if (isNaN(curB)) curB = 0;
                    }
                    var newB = Math.max(0, curB + delta);
                    block.style.marginLeft = newB + 'px';
                }
            });

            // 🔹 NEW: restore the exact selection on the next tick and keep savedSelection in sync
            setTimeout(() => {
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(beforeRange);
                if (savedSelection && savedSelection.current !== undefined) {
                    savedSelection.current = beforeRange; // keep your ref aligned
                }
                // fire input after selection is stable
                setTimeout(handleInput, 0);
            }, 0);
        };


        function placeCaret(el, atEnd) {
            if (!el) return;
            const sel = window.getSelection();
            const r = document.createRange();
            r.selectNodeContents(el);
            r.collapse(!!atEnd);
            sel.removeAllRanges();
            sel.addRange(r);
        }

        const handleCustomIndent = () => adjustParagraphIndent(10);
        const handleCustomOutdent = () => adjustParagraphIndent(-10);

        const clearFormatting = () => {
            restoreSelection();

            const selection = window.getSelection();
            if (!selection || !selection.rangeCount || selection.isCollapsed) return;

            const editor = editorRef.current;
            const range = selection.getRangeAt(0);
            if (!editor || !editor.contains(range.commonAncestorContainer)) return;

            // ---------- helpers ----------
            function topChildUnderEditor(node) {
                var el = node.nodeType === 1 ? node : node.parentElement;
                while (el && el.parentElement && el.parentElement !== editor) {
                    el = el.parentElement;
                }
                return (el && el.parentElement === editor) ? el : null;
            }

            function unwrapInline(el) {
                var p = el.parentNode;
                while (el.firstChild) p.insertBefore(el.firstChild, el);
                p.removeChild(el);
            }

            function cleanupEmptyInlineAncestors(start) {
                var el = start && start.parentElement;
                while (el && el !== editor) {
                    var t = el.tagName;
                    if ((t === 'SPAN' || t === 'B' || t === 'I' || t === 'U' || t === 'EM' || t === 'STRONG' || t === 'FONT') &&
                        el.textContent.replace(/\u200B/g, '').trim() === '' && el.children.length === 0) {
                        var parent = el.parentElement;
                        parent.removeChild(el);
                        el = parent;
                    } else {
                        el = el.parentElement;
                    }
                }
            }

            function isEffectivelyEmpty(el) {
                return el.textContent.replace(/\u200B/g, '').trim().length === 0 && el.children.length === 0;
            }

            // -----------------------------

            // Anchor: nearest direct child of editor that contains the start of selection
            const anchor = topChildUnderEditor(range.startContainer) ||
                topChildUnderEditor(range.commonAncestorContainer) ||
                editor.lastChild;

            // Extract the selection from the DOM (removes it from its parent)
            const extracted = range.extractContents();

            // Clean & flatten the extracted fragment into <p> nodes
            const tmp = document.createElement('div');
            tmp.appendChild(extracted);

            // Remove styles/classes globally in fragment
            tmp.querySelectorAll('*').forEach(function (el) {
                el.removeAttribute('style');
                el.removeAttribute('class');
            });

            // Unwrap common inline wrappers
            tmp.querySelectorAll('span,b,i,u,strong,em,font').forEach(unwrapInline);

            // Convert H1-6, LI, DIV to P
            ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'DIV'].forEach(function (tag) {
                const nodes = tmp.querySelectorAll(tag);
                for (var i = 0; i < nodes.length; i++) {
                    const el = nodes[i];
                    const p = document.createElement('p');
                    p.innerHTML = el.innerHTML;
                    el.replaceWith(p);
                }
            });

            // Flatten UL/OL into P
            const lists = tmp.querySelectorAll('ul,ol');
            for (var i = 0; i < lists.length; i++) {
                const list = lists[i];
                const newNodes = [];
                for (var j = 0; j < list.childNodes.length; j++) {
                    const child = list.childNodes[j];
                    if (child.nodeType === 1 && child.tagName === 'LI') {
                        const p = document.createElement('p');
                        p.innerHTML = child.innerHTML;
                        newNodes.push(p);
                    } else if (child.nodeType === 3 && child.textContent.trim()) {
                        const p = document.createElement('p');
                        p.textContent = child.textContent;
                        newNodes.push(p);
                    }
                }
                list.replaceWith.apply(list, newNodes);
            }

            // Build final <p> list, splitting on <br> and wrapping stray text
            const finalPs = [];
            (function buildPs(container) {
                var cur = document.createElement('p');
                var hasText = false;

                function pushCur() {
                    if (hasText) {
                        // trim leading nbsp/spaces
                        if (cur.firstChild && cur.firstChild.nodeType === 3) {
                            cur.firstChild.nodeValue = cur.firstChild.nodeValue.replace(/^[\u00A0\s]+/, '');
                        }
                        finalPs.push(cur);
                    }
                    cur = document.createElement('p');
                    hasText = false;
                }

                var nodes = Array.prototype.slice.call(container.childNodes);
                for (var k = 0; k < nodes.length; k++) {
                    var n = nodes[k];
                    if (n.nodeType === 1 && n.tagName === 'P') {
                        // finish current and push this P as-is
                        pushCur();
                        finalPs.push(n);
                        cur = document.createElement('p');
                        hasText = false;
                    } else if (n.nodeType === 1 && n.tagName === 'BR') {
                        pushCur();
                    } else if (n.nodeType === 1) {
                        // other element: move it into current p
                        cur.appendChild(n);
                        if (n.textContent && n.textContent.replace(/\u00A0/g, '').trim() !== '') hasText = true;
                    } else if (n.nodeType === 3) {
                        if (n.nodeValue && n.nodeValue.replace(/\u00A0/g, '').trim() !== '') {
                            cur.appendChild(n);
                            hasText = true;
                        }
                    }
                }
                pushCur();
            })(tmp);

            if (finalPs.length === 0) {
                // Nothing meaningful selected; bail
                handleInput();
                return;
            }

            // Insert cleaned <p> nodes as siblings right AFTER the anchor
            const frag = document.createDocumentFragment();
            for (var a = 0; a < finalPs.length; a++) {
                var pnode = finalPs[a];
                pnode.removeAttribute('style');
                pnode.removeAttribute('class');
                // ensure not empty
                if (isEffectivelyEmpty(pnode)) continue;
                frag.appendChild(pnode);
            }

            if (anchor && anchor.parentNode === editor) {
                if (anchor.nextSibling) editor.insertBefore(frag, anchor.nextSibling);
                else editor.appendChild(frag);
            } else {
                editor.appendChild(frag);
            }

            // Cleanup: remove empty inline wrappers left behind near the start
            cleanupEmptyInlineAncestors(range.startContainer);

            // Cleanup: if an inline now has leading <br> before a list, drop that <br>
            var inlineStart = range.startContainer.nodeType === 1 ? range.startContainer : range.startContainer.parentElement;
            if (inlineStart && inlineStart.firstChild && inlineStart.firstChild.tagName === 'BR') {
                var nx = inlineStart.firstChild.nextSibling;
                if (nx && nx.nodeType === 1 && (nx.tagName === 'OL' || nx.tagName === 'UL')) {
                    inlineStart.removeChild(inlineStart.firstChild);
                }
            }

            // Remove empty LIs, empty lists, and empty Ps created by extraction
            var allLis = editor.querySelectorAll('li');
            for (var lii = 0; lii < allLis.length; lii++) if (isEffectivelyEmpty(allLis[lii])) allLis[lii].parentNode.removeChild(allLis[lii]);

            var allLists = editor.querySelectorAll('ul,ol');
            for (var li = 0; li < allLists.length; li++) if (!allLists[li].querySelector('li')) allLists[li].parentNode.removeChild(allLists[li]);

            var allBlocks = editor.querySelectorAll('p,h1,h2,h3,h4,h5,h6,div');
            for (var bi = 0; bi < allBlocks.length; bi++) if (isEffectivelyEmpty(allBlocks[bi])) allBlocks[bi].parentNode.removeChild(allBlocks[bi]);

            // Restore caret at end of the last inserted <p>
            selection.removeAllRanges();
            var lastInserted = editor.lastChild;
            while (lastInserted && !(lastInserted.nodeType === 1 && lastInserted.tagName === 'P')) {
                lastInserted = lastInserted.previousSibling;
            }
            const newRange = document.createRange();
            if (lastInserted) {
                newRange.selectNodeContents(lastInserted);
                newRange.collapse(false);
            } else {
                newRange.selectNodeContents(editor);
                newRange.collapse(false);
            }
            selection.addRange(newRange);

            handleInput();
        };


// Treat LI as empty if it has only whitespace, NBSPs, zero-width, and/or <br>
        const isEmptyLi = function (li) {
            if (!li) return false;
            // clone & strip <br> for a reliable text check
            var clone = li.cloneNode(true);
            var brs = clone.querySelectorAll('br');
            for (var i = 0; i < brs.length; i++) brs[i].parentNode.removeChild(brs[i]);

            var txt = (clone.textContent || '')
                .replace(/\u200B/g, '')     // zero-width space
                .replace(/\u00A0/g, '')     // NBSP
                .replace(/\s+/g, '');       // regular whitespace

            var hasMedia = li.querySelector('img,table,video,canvas,figure');
            return txt.length === 0 && !hasMedia;
        };


        const placeCaretAt = (node, atEnd) => {
            var sel = window.getSelection();
            var range = document.createRange();
            range.selectNodeContents(node);
            range.collapse(!atEnd);
            sel.removeAllRanges();
            sel.addRange(range);
        };

        const splitListAtLi = (li) => {
            var list = li.parentElement;
            var after = [];
            var sib = li.nextSibling;
            while (sib) {
                after.push(sib);
                sib = sib.nextSibling;
            }

            li.parentElement && li.parentElement.removeChild(li);

            var p = document.createElement('p');
            p.innerHTML = '&nbsp;';

            if (after.length) {
                var newList = list.cloneNode(false); // UL/OL shell only
                for (var i = 0; i < after.length; i++) newList.appendChild(after[i]);
                list.parentNode.insertBefore(p, list.nextSibling);
                list.parentNode.insertBefore(newList, p.nextSibling);
            } else {
                list.parentNode.insertBefore(p, list.nextSibling);
            }

            if (!list.firstElementChild) list.parentNode && list.parentNode.removeChild(list);
            return p;
        };

// ✅ No optional chaining here
        const getClosestLi = (node) => {
            var el = node;
            if (!el) return null;
            if (el.nodeType !== 1) el = el.parentElement;
            while (el && el.tagName !== 'LI') el = el.parentElement;
            return el || null;
        };


// Soft break with <br> + empty text node (no ZWS)
// Keeps caret in-place and leaves no printable artifacts.
// Stable soft break: <br> + empty text node; no zero-width chars, caret stays put
        var insertSoftBreak = function () {
            var sel = window.getSelection();
            var editor = editorRef.current;
            if (!sel || !sel.rangeCount || !editor) return;

            var range = sel.getRangeAt(0);
            if (!editor.contains(range.commonAncestorContainer)) return;

            // delete selected content and insert <br> + empty text node
            range.deleteContents();

            var br = document.createElement('br');
            var anchor = document.createTextNode(''); // empty text node (prints nothing)

            range.insertNode(br);
            if (br.parentNode) br.parentNode.insertBefore(anchor, br.nextSibling);

            // function to place caret inside the empty text node
            var setCaret = function () {
                var r = document.createRange();
                r.setStart(anchor, 0);
                r.collapse(true);
                sel.removeAllRanges();
                sel.addRange(r);
                editor.focus();
            };

            // set now and re-assert next frame to beat React/handlers
            setCaret();
            requestAnimationFrame(setCaret);

            // let DOM settle before emitting input (prevents selection resets)
            setTimeout(function () {
                handleInput();
            }, 0);
        };


        var purgeZeroWidth = function (root) {
            if (!root) return;
            var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
            var n;
            var re = /[\u200B\u200C\u200D\uFEFF]/g;
            while ((n = walker.nextNode())) {
                if (re.test(n.nodeValue)) {
                    n.nodeValue = n.nodeValue.replace(re, '');
                }
            }
        };


        const handleKeyDown = (e) => {
            if (e.key !== 'Enter') return;

            restoreSelection();
            var sel = window.getSelection();
            if (!sel || !sel.rangeCount) return;

            var anchor = sel.anchorNode;
            var li = getClosestLi(anchor);  // ⬅️ replaces anchor?.nodeType... line

            if (!li) return;

            // Shift+Enter -> soft line break
            if (e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                insertSoftBreak()
                return;
            }

            // Enter on empty LI -> exit list
            if (isEmptyLi(li)) {
                e.preventDefault();

                var parentList = li.parentElement;             // current <ul>/<ol>
                var parentLi = parentList.parentElement;       // might be an <li> if nested

                if (parentLi && parentLi.tagName === 'LI') {
                    // ✅ We're in a sub list — outdent instead of exiting completely
                    var grandList = parentLi.parentElement; // the list containing the parent li
                    grandList.insertBefore(li, parentLi.nextSibling);

                    // focus the now-top-level li
                    placeCaretAt(li, false);

                    // remove empty sublist if needed
                    if (!parentList.firstElementChild) parentList.remove();
                } else {
                    // regular exit logic (same as before)
                    var p = splitListAtLi(li);
                    placeCaretAt(p, false);
                }

                handleInput();
                return;
            }

            // otherwise let browser create a new <li>
        };

        const alignLeftNow = function () {
            restoreSelection();

            var sel = window.getSelection();
            var editor = editorRef.current;
            if (!editor) return;

            // Find the nearest block (P/DIV) from a node
            function findBlockFrom(node) {
                var el = node;
                if (!el) return null;
                if (el.nodeType !== 1) el = el.parentElement;
                while (el && el !== editor && !(el.tagName === 'P' || el.tagName === 'DIV')) {
                    el = el.parentElement;
                }
                return (el && (el.tagName === 'P' || el.tagName === 'DIV')) ? el : null;
            }

            // Robust intersects helper (works even if browser lacks Range.intersectsNode)
            function intersectsRange(node, range) {
                if (!node || !range) return false;
                if (typeof range.intersectsNode === 'function') {
                    try {
                        return range.intersectsNode(node);
                    } catch (e) { /* fall through */
                    }
                }
                var r = document.createRange();
                try {
                    r.selectNode(node);
                } catch (e) {
                    return false;
                }
                return !(r.compareBoundaryPoints(Range.END_TO_START, range) <= 0 ||
                    r.compareBoundaryPoints(Range.START_TO_END, range) >= 0);
            }

            // Collect all P/DIV blocks that intersect the selection range
            function blocksIntersecting(range) {
                var out = [];
                var walker = document.createTreeWalker(
                    editor,
                    NodeFilter.SHOW_ELEMENT,
                    {
                        acceptNode: function (node) {
                            var tag = node.tagName;
                            if ((tag === 'P' || tag === 'DIV') && intersectsRange(node, range)) {
                                return NodeFilter.FILTER_ACCEPT;
                            }
                            return NodeFilter.FILTER_SKIP;
                        }
                    },
                    false
                );
                var n = walker.nextNode();
                while (n) {
                    out.push(n);
                    n = walker.nextNode();
                }
                return out;
            }

            // Clear inline align props ONLY for elements that intersect the range
            function clearInlineAlignInside(block, range) {
                var walker = document.createTreeWalker(
                    block,
                    NodeFilter.SHOW_ELEMENT,
                    {
                        acceptNode: function (node) {
                            if (node === block) return NodeFilter.FILTER_SKIP;
                            var styleAttr = node.getAttribute && node.getAttribute('style');
                            if (styleAttr && (styleAttr.indexOf('text-align') !== -1 ||
                                styleAttr.indexOf('text-indent') !== -1)) {
                                return range ? (intersectsRange(node, range) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP)
                                    : NodeFilter.FILTER_ACCEPT;
                            }
                            return NodeFilter.FILTER_SKIP;
                        }
                    },
                    false
                );
                var n = walker.nextNode();
                while (n) {
                    n.style.textAlign = '';
                    n.style.textIndent = '';
                    n = walker.nextNode();
                }
            }

            if (!sel || !sel.rangeCount) return;

            var range = sel.getRangeAt(0);
            if (!editor.contains(range.commonAncestorContainer)) return;

            if (sel.isCollapsed) {
                // Align just the current block
                var block = findBlockFrom(range.startContainer);
                if (!block) return;

                block.style.textAlign = 'left';
                block.style.textAlignLast = '';
                // do NOT touch margins/padding/indent globally anymore

                // Clean inline alignment only inside caret’s block (no range → whole block is fine)
                clearInlineAlignInside(block, null);

                handleInput();
                return;
            }

            // Expanded selection: align only the blocks that intersect the selection
            var targets = blocksIntersecting(range);
            if (targets.length === 0) {
                // Fallback to single block if nothing matched (edge cases)
                var single = findBlockFrom(range.startContainer);
                if (!single) return;
                targets = [single];
            }

            for (var i = 0; i < targets.length; i++) {
                var blk = targets[i];
                blk.style.textAlign = 'left';
                blk.style.textAlignLast = '';
                // Don’t reset margins/padding on the whole block; keep indent intact

                // Now clear inline overrides ONLY where the selection actually touches
                clearInlineAlignInside(blk, range);
            }

            // Keep user selection as-is
            handleInput();
        };


        var getActiveBlock = function (node, editor) {
            var el = node;
            if (!el) return null;
            if (el.nodeType !== 1) el = el.parentElement;
            while (el && el !== editor && ['P', 'DIV', 'LI'].indexOf(el.tagName) === -1) el = el.parentElement;
            return el && (el.tagName === 'P' || el.tagName === 'DIV') ? el : null;
        };
// ===== Alpha-repeat helpers: a..z, then aa, bb, cc... =====
        const _alphaRepeatObservers = new WeakMap();

        function _computeAlphaRepeatLabel(n /* 1-based */) {
            const repeats = Math.floor((n - 1) / 26) + 1;      // 1→26 => 1; 27→52 => 2; etc
            const pos = ((n - 1) % 26) + 1;                // 1..26
            const ch = String.fromCharCode(96 + pos);     // 'a'..'z'
            return `${ch.repeat(repeats)}. `;
        }

        function renumberAlphaRepeat(ol) {
            if (!ol) return;
            const items = Array.from(ol.children).filter(n => n.nodeName === 'LI');
            items.forEach((li, i) => {
                li.style.setProperty('--marker', `"${_computeAlphaRepeatLabel(i + 1)}"`);
            });
        }

        function ensureAlphaRepeat(ol) {
            if (!ol) return;
            // force <ol> (convert UL to OL if needed)
            if (ol.tagName !== 'OL') {
                const replacement = document.createElement('OL');
                // copy attrs
                for (let i = 0; i < ol.attributes.length; i++) {
                    const a = ol.attributes[i];
                    if (a.name !== 'style') replacement.setAttribute(a.name, a.value);
                }
                replacement.style.cssText = ol.style.cssText;
                while (ol.firstChild) replacement.appendChild(ol.firstChild);
                ol.replaceWith(replacement);
                ol = replacement;
            }

            ol.classList.add('alpha-repeat');
            renumberAlphaRepeat(ol);

            // Observe mutations to keep numbering in sync
            if (_alphaRepeatObservers.has(ol)) return;

            const mo = new MutationObserver(() => renumberAlphaRepeat(ol));
            mo.observe(ol, {childList: true, subtree: false});
            _alphaRepeatObservers.set(ol, mo);
        }

        function disableAlphaRepeat(ol) {
            if (!ol) return;
            ol.classList.remove('alpha-repeat');
            // clear per-li markers
            ol.querySelectorAll(':scope > li').forEach(li => li.style.removeProperty('--marker'));
            const mo = _alphaRepeatObservers.get(ol);
            if (mo) {
                mo.disconnect();
                _alphaRepeatObservers.delete(ol);
            }
        }

        function renumberAllAlphaRepeat(root) {
            if (!root) return;
            root.querySelectorAll('ol.alpha-repeat').forEach(renumberAlphaRepeat);
        }


        return (
            <div className="editor-wrapper" {...containerProps}>
                <div className="editor-toolbar">
                    <select value={formatBlock} onChange={handleFormatBlockChange} onMouseDown={saveSelection}>
                        <option value="p">Normal text</option>
                        <option value="h1">Heading 1</option>
                        <option value="h2">Heading 2</option>
                        <option value="h3">Heading 3</option>
                    </select>

                    <select value={fontFamily} onChange={handleFontFamilyChange} onMouseDown={saveSelection}>
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                    </select>

                    <input
                        type="number"
                        value={fontSize}
                        min="1"
                        max="7"
                        onChange={handleFontSizeChange}
                        onMouseDown={saveSelection}
                    />

                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('bold')} title="Bold">
                        <span>B</span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('italic')} title="Italic">
                        <span>I</span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('underline')} title="Underline">
                        <span>U</span>
                    </button>

                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('disc', 'insertUnorderedList')}
                            title="Bulleted List (Disc)">
                        <span>•</span>
                    </button>
                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('circle', 'insertUnorderedList')}
                            title="Bulleted List (Circle)">
                        <span>○</span>
                    </button>
                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('square', 'insertUnorderedList')}
                            title="Bulleted List (Square)">
                        <span>▪</span>
                    </button>
                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('decimal', 'insertOrderedList')}
                            title="Numbered List (1)">
                        <span>1.</span>
                    </button>
                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('lower-alpha', 'insertOrderedList')}
                            title="Numbered List (a)">
                        <span>a.</span>
                    </button>
                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('upper-alpha', 'insertOrderedList')}
                            title="Numbered List (A)">
                        <span>A.</span>
                    </button>

                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('upper-roman', 'insertOrderedList')}
                            title="Numbered List (I)">
                        <span>I.</span>
                    </button>
                    <button onMouseDown={saveSelection}
                            onClick={() => forceStartListAtCursor('lower-roman', 'insertOrderedList')}
                            title="Numbered List (i)">
                        <span>i.</span>
                    </button>

                    <button onMouseDown={saveSelection} onClick={alignLeftNow} title="Align Left">
                        <span className="icon-align-left"></span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('justifyCenter')}
                            title="Align Center">
                        <span className="icon-align-center"></span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('justifyRight')}
                            title="Align Right">
                        <span className="icon-align-right"></span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('justifyFull')} title="Justify">
                        <span className="icon-align-justify"></span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('indent')} title="Indent">
                        <span className="icon-indent"></span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('outdent')} title="Outdent">
                        <span className="icon-outdent"></span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('undo')} title="Undo">
                        <span>↶</span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={() => execWithSelection('redo')} title="Redo">
                        <span>↷</span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={handleCustomIndent} title="Custom Indent">
                        <span>⬅</span>
                    </button>
                    <button onMouseDown={saveSelection} onClick={handleCustomOutdent} title="Custom Outdent">
                        <span>➡</span>
                    </button>
                    <button
                        onMouseDown={saveSelection}
                        onClick={() => forceStartListAtCursor('lower-alpha-repeat', 'insertOrderedList')}
                        title="Numbered List (a, …, z, aa, bb, …)"
                    >
                        <span>a→aa</span>
                    </button>

                    <button onMouseDown={saveSelection} onClick={clearFormatting} title="Clear Formatting">
                        <span>✖</span>
                    </button>
                </div>
                <div
                    className="editor-box"
                    contentEditable
                    ref={editorRef}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onMouseUp={() => {
                        saveSelection();
                        renumberAllAlphaRepeat(editorRef.current);
                        syncFontSizeUI()
                    }}
                    onKeyUp={() => {
                        saveSelection();
                        renumberAllAlphaRepeat(editorRef.current);
                        syncFontSizeUI()
                    }}
                    onFocus={() => {
                        isFocused.current = true;
                    }}
                    onBlur={() => {
                        isFocused.current = false;
                        saveSelection();
                    }}
                    onKeyDown={handleKeyDown}
                />
            </div>
        );
    }
;

export default CustomEditor;